import React, { useMemo, useState, useEffect } from 'react';
import { Card } from '../components/ui/Card';
import { usePacientes } from '../hooks/usePacientes';
import { useAuth } from '../contexts/AuthContext';
import { ref, get } from '../services/phpDataAdapter';
import { database } from '../config/backend';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import './Pages.css';

interface Measurement {
  date: string;
  weight: number;
  timestamp: Date;
}

interface AdherenceData {
  date: string;
  score: number;
}

interface PatientAdherence {
  id: string;
  nome: string;
  adherence: number;
}

const COLORS = ['#2a9d8f', '#0ea5e9', '#f59e0b', '#ef4444', '#10b981'];

function getDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export const Reports: React.FC = () => {
  const { user } = useAuth();
  const { pacientes, loading } = usePacientes();
  const [measurements, setMeasurements] = useState<Measurement[]>([]);
  const [adherenceData, setAdherenceData] = useState<AdherenceData[]>([]);
  const [patientAdherence, setPatientAdherence] = useState<PatientAdherence[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');

  useEffect(() => {
    loadReportData();
  }, [pacientes, timeRange, user]);

  const loadReportData = async () => {
    if (!user || pacientes.length === 0) {
      setLoadingData(false);
      return;
    }

    setLoadingData(true);
    try {
      const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      // Buscar medições de peso de todos os pacientes
      const allMeasurements: Measurement[] = [];
      const allAdherence: AdherenceData[] = [];
      const patientAdherenceList: PatientAdherence[] = [];

      await Promise.all(
        pacientes.map(async (paciente) => {
          try {
            // Buscar medições
            const measurementsRef = ref(database, `users/${paciente.id}/measurements`);
            const measurementsSnap = await get(measurementsRef);

            if (measurementsSnap.exists()) {
              const measurementsData = measurementsSnap.val() as Record<string, any>;
              Object.entries(measurementsData).forEach(([id, data]) => {
                const timestamp = data.timestamp
                  ? new Date(data.timestamp)
                  : data.date
                  ? new Date(data.date)
                  : new Date(data.createdAt || 0);

                if (timestamp >= startDate && timestamp <= endDate && data.weight) {
                  allMeasurements.push({
                    date: timestamp.toISOString(),
                    weight: Number(data.weight) || 0,
                    timestamp,
                  });
                }
              });
            }

            // Buscar adesão dos últimos 7 dias para média
            let totalScore = 0;
            let scoreCount = 0;
            const today = new Date();

            for (let i = 0; i < 7; i++) {
              const date = new Date(today);
              date.setDate(date.getDate() - i);
              const dateKey = getDateKey(date);

              try {
                const scoreSnap = await get(
                  ref(database, `users/${paciente.id}/dietTracking/${dateKey}/dailyScore`)
                );

                if (scoreSnap.exists()) {
                  const score = Number(scoreSnap.val()) || 0;
                  totalScore += score;
                  scoreCount++;

                  if (i < days) {
                    allAdherence.push({
                      date: date.toISOString().split('T')[0],
                      score,
                    });
                  }
                }
              } catch {
                // Ignora erros
              }
            }

            const avgAdherence = scoreCount > 0 ? Math.round(totalScore / scoreCount) : 0;
            patientAdherenceList.push({
              id: paciente.id,
              nome: paciente.nome,
              adherence: avgAdherence,
            });
          } catch (error) {
            console.error(`Erro ao carregar dados do paciente ${paciente.id}:`, error);
          }
        })
      );

      // Ordenar medições por data
      allMeasurements.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

      // Agrupar medições por dia (média)
      const measurementsByDate = new Map<string, number[]>();
      allMeasurements.forEach((m) => {
        const dateKey = m.timestamp.toISOString().split('T')[0];
        if (!measurementsByDate.has(dateKey)) {
          measurementsByDate.set(dateKey, []);
        }
        measurementsByDate.get(dateKey)!.push(m.weight);
      });

      const groupedMeasurements: Measurement[] = Array.from(measurementsByDate.entries()).map(
        ([date, weights]) => ({
          date,
          weight: weights.reduce((a, b) => a + b, 0) / weights.length,
          timestamp: new Date(date),
        })
      );

      setMeasurements(groupedMeasurements);
      setAdherenceData(allAdherence);
      setPatientAdherence(patientAdherenceList);
    } catch (error) {
      console.error('Erro ao carregar dados dos relatórios:', error);
    } finally {
      setLoadingData(false);
    }
  };

  const statusDistribution = useMemo(() => {
    if (!pacientes.length) {
      return {
        ativo: 0,
        avaliacao: 0,
        alerta: 0,
      };
    }
    const total = pacientes.length;
    const ativo = pacientes.filter((p) => p.status === 'ativo').length;
    const avaliacao = pacientes.filter((p) => p.status === 'avaliacao').length;
    const alerta = pacientes.filter((p) => p.status === 'alerta').length;

    const toPercent = (v: number) => Math.round((v / total) * 100);

    return {
      ativo: toPercent(ativo),
      avaliacao: toPercent(avaliacao),
      alerta: toPercent(alerta),
    };
  }, [pacientes]);

  const statusChartData = useMemo(() => {
    return [
      { name: 'Ativo', value: pacientes.filter((p) => p.status === 'ativo').length, color: '#10b981' },
      { name: 'Avaliação', value: pacientes.filter((p) => p.status === 'avaliacao').length, color: '#f59e0b' },
      { name: 'Alerta', value: pacientes.filter((p) => p.status === 'alerta').length, color: 'var(--theme-danger)' },
    ].filter((item) => item.value > 0);
  }, [pacientes]);

  const adherenceChartData = useMemo(() => {
    // Agrupar adesão por data
    const adherenceByDate = new Map<string, number[]>();
    adherenceData.forEach((item) => {
      if (!adherenceByDate.has(item.date)) {
        adherenceByDate.set(item.date, []);
      }
      adherenceByDate.get(item.date)!.push(item.score);
    });

    return Array.from(adherenceByDate.entries())
      .map(([date, scores]) => ({
        date: new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
        score: Math.round(scores.reduce((a, b) => a + b, 0) / scores.length),
      }))
      .sort((a, b) => new Date(a.date.split('/').reverse().join('-')).getTime() - new Date(b.date.split('/').reverse().join('-')).getTime())
      .slice(-30); // Últimos 30 pontos
  }, [adherenceData]);

  const patientAdherenceChartData = useMemo(() => {
    return patientAdherence
      .sort((a, b) => b.adherence - a.adherence)
      .slice(0, 10)
      .map((p) => ({
        name: p.nome.length > 15 ? p.nome.substring(0, 15) + '...' : p.nome,
        adherence: p.adherence,
      }));
  }, [patientAdherence]);

  const weightChartData = useMemo(() => {
    return measurements.map((m) => ({
      date: new Date(m.date).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
      peso: Math.round(m.weight * 10) / 10,
    }));
  }, [measurements]);

  const alerts = useMemo(() => {
    const alertsList: Array<{ type: 'warning' | 'danger'; message: string }> = [];

    // Verificar pacientes com baixa adesão
    const lowAdherence = patientAdherence.filter((p) => p.adherence < 70);
    if (lowAdherence.length > 0) {
      alertsList.push({
        type: 'warning',
        message: `${lowAdherence.length} paciente(s) com adesão abaixo de 70%`,
      });
    }

    // Verificar pacientes em alerta
    const alertPatients = pacientes.filter((p) => p.status === 'alerta');
    if (alertPatients.length > 0) {
      alertsList.push({
        type: 'danger',
        message: `${alertPatients.length} paciente(s) em situação de alerta`,
      });
    }

    // Verificar se há poucas medições
    if (measurements.length < 5 && pacientes.length > 0) {
      alertsList.push({
        type: 'warning',
        message: 'Poucas medições registradas. Incentive os pacientes a registrar medições regularmente.',
      });
    }

    return alertsList;
  }, [patientAdherence, pacientes, measurements]);

  return (
    <div className="ns-page">
      <div className="ns-page-header">
        <div>
          <h2>Relatórios</h2>
          <p>Acompanhe a evolução dos seus pacientes com base nos dados reais.</p>
        </div>
        <div className="ns-page-header-actions">
          <select
            className="ns-input"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as '7d' | '30d' | '90d')}
            style={{ width: 'auto', minWidth: '120px' }}
          >
            <option value="7d">Últimos 7 dias</option>
            <option value="30d">Últimos 30 dias</option>
            <option value="90d">Últimos 90 dias</option>
          </select>
        </div>
      </div>

      {/* Alertas */}
      {alerts.length > 0 && (
        <div className="ns-reports-alerts">
          {alerts.map((alert, index) => (
            <div key={index} className={`ns-alert ns-alert-${alert.type}`}>
              <span className="ns-alert-icon">
                {alert.type === 'danger' ? '⚠️' : '⚠️'}
              </span>
              <span className="ns-alert-message">{alert.message}</span>
            </div>
          ))}
        </div>
      )}

      <div className="ns-grid ns-grid-2">
        {/* Gráfico de Evolução de Peso */}
        <Card title="Evolução de Peso Média" subtitle={`Período: ${timeRange === '7d' ? '7 dias' : timeRange === '30d' ? '30 dias' : '90 dias'}`}>
          {loadingData ? (
            <div className="ns-chart-loading">Carregando dados...</div>
          ) : weightChartData.length === 0 ? (
            <div className="ns-chart-empty">
              <p>Nenhuma medição de peso encontrada no período selecionado.</p>
              <p className="ns-chart-empty-hint">
                As medições registradas pelos pacientes no app mobile aparecerão aqui.
              </p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={weightChartData}>
                <defs>
                  <linearGradient id="colorWeight" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2a9d8f" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#2a9d8f" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="date"
                  stroke="#64748b"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis stroke="#64748b" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="peso"
                  stroke="#2a9d8f"
                  strokeWidth={2}
                  fill="url(#colorWeight)"
                  name="Peso (kg)"
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Gráfico de Status dos Pacientes */}
        <Card title="Status dos Pacientes" subtitle="Distribuição geral">
          {loading ? (
            <div className="ns-chart-loading">Carregando dados...</div>
          ) : statusChartData.length === 0 ? (
            <div className="ns-chart-empty">
              <p>Nenhum paciente cadastrado ainda.</p>
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={statusChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <ul className="ns-report-list">
                <li>
                  <span>Em acompanhamento ativo</span>
                  <strong>{statusDistribution.ativo}%</strong>
                </li>
                <li>
                  <span>Em avaliação inicial</span>
                  <strong>{statusDistribution.avaliacao}%</strong>
                </li>
                <li>
                  <span>Em alerta / baixa adesão</span>
                  <strong>{statusDistribution.alerta}%</strong>
                </li>
              </ul>
            </>
          )}
        </Card>
      </div>

      <div className="ns-grid ns-grid-2">
        {/* Gráfico de Adesão ao Plano */}
        <Card title="Adesão ao Plano Alimentar" subtitle="Score médio diário">
          {loadingData ? (
            <div className="ns-chart-loading">Carregando dados...</div>
          ) : adherenceChartData.length === 0 ? (
            <div className="ns-chart-empty">
              <p>Nenhum dado de adesão encontrado no período selecionado.</p>
              <p className="ns-chart-empty-hint">
                A adesão é calculada com base no registro de refeições pelos pacientes.
              </p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={adherenceChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="date"
                  stroke="#64748b"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  stroke="#64748b"
                  style={{ fontSize: '12px' }}
                  domain={[0, 100]}
                  label={{ value: 'Score (%)', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke="#0ea5e9"
                  strokeWidth={2}
                  dot={{ fill: '#0ea5e9', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="Adesão (%)"
                />
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Gráfico de Adesão por Paciente */}
        <Card title="Adesão por Paciente" subtitle="Top 10 pacientes">
          {loadingData ? (
            <div className="ns-chart-loading">Carregando dados...</div>
          ) : patientAdherenceChartData.length === 0 ? (
            <div className="ns-chart-empty">
              <p>Nenhum dado de adesão disponível.</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={patientAdherenceChartData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" domain={[0, 100]} stroke="#64748b" style={{ fontSize: '12px' }} />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke="#64748b"
                  style={{ fontSize: '12px' }}
                  width={100}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                  }}
                  formatter={(value: number) => [`${value}%`, 'Adesão']}
                />
                <Bar
                  dataKey="adherence"
                  fill="#2a9d8f"
                  radius={[0, 8, 8, 0]}
                  name="Adesão (%)"
                >
                  {patientAdherenceChartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.adherence >= 80 ? '#10b981' : entry.adherence >= 70 ? '#f59e0b' : '#ef4444'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
    </div>
  );
};
